import { eq, and, sql, inArray, isNull } from 'drizzle-orm';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import { db } from '@/db/client';
import {
    accounts,
    budgets,
    budgetCategories,
    budgetOrigins,
    budgetSubcategories,
    budgetSubcategoryLimits,
    categories,
    creditCards,
    subcategories,
    transactions,
} from '@/db/schema';
import { generateId } from '@/lib/id';
import { roundMonetaryValue } from '@/lib/formatting';
import { ExpectedError } from '@/lib/errorUtils';
import i18n from '@/lib/i18n';
import {
    CALENDAR_MONTH_START_DAY,
    monthCycleForKey,
    monthCycleLength,
    monthKeyForDate,
    monthKeysBetween,
} from '@/lib/monthCycle';
import { getMonthStartDay } from '@/lib/monthStart';
import {
    cycleForMonth,
    cyclesEnvelope,
    cyclesOfMonth,
    cycleContaining,
    cycleMonth,
    dateInCycle,
    normalizeCycleSpec,
    type BudgetCycle,
} from '@/lib/budgetCycle';
import {
    excludeDeletedCardTransactions,
    excludeIgnored,
    excludeNonAnalyticInvoiceAdjustments,
    excludePersonalLoanPrincipal,
    originCardIdExpr,
} from './transactionScopes';

export type Budget = typeof budgets.$inferSelect;

/** Uma categoria que compõe um orçamento (com dados de exibição). */
export interface BudgetCategoryRef {
    categoryId: string;
    name: string;
    color: string;
    icon: string;
}

/**
 * Um Limite por subcategoria (ADR 0067): recorte interno do orçamento, com o nome da
 * subcategoria e a categoria dela (pra agrupar no form).
 */
export interface BudgetSubcategoryLimitRef {
    subcategoryId: string;
    categoryId: string;
    name: string;
    amount: number;
}

/**
 * Uma Subcategoria em que o orçamento vigia (ADR 0110), com a categoria dela pra agrupar.
 * O recorte é POR CATEGORIA: categoria sem linha aqui conta inteira.
 */
export interface BudgetSubcategoryRef {
    subcategoryId: string;
    categoryId: string;
    name: string;
}

/** Conta ou Cartão em que o orçamento vigia (ADR 0103), com dados de exibição. */
export interface BudgetOriginRef {
    kind: 'account' | 'card';
    id: string;
    name: string;
    /**
     * O que o chip mostra é o LOGO da instituição (o mesmo do carrossel de cartões e do
     * seletor de conta), não um ícone genérico: um chip com carteirinha cinza não diz de
     * qual banco é o recorte. `InstitutionLogo` cai num glifo neutro quando não resolve.
     */
    institution: string | null;
    institutionIcon: string | null;
    /** Só cartão: fallback de bandeira (Visa/Master) quando a instituição não resolve. */
    brand: string | null;
}

/** O que o form manda: só o par tipo+id; o resto é resolvido na leitura. */
export interface BudgetOriginInput {
    kind: 'account' | 'card';
    id: string;
}

export type BudgetWithCategories = Budget & {
    categories: BudgetCategoryRef[];
    /** Limites por subcategoria. Sempre vazio em meta de receita (ADR 0067). */
    subcategoryLimits: BudgetSubcategoryLimitRef[];
    /**
     * Subcategorias em que o orçamento vigia (ADR 0110). **Vazio = a categoria inteira**,
     * por categoria: "Alimentação só em Energético" + Transporte sem linha = Transporte
     * inteiro. Sempre vazio em meta de receita, como o limite.
     */
    subcategories: BudgetSubcategoryRef[];
    /**
     * Origens em que o orçamento vigia (ADR 0103). **Vazio = TODAS** — é o orçamento
     * clássico e o estado de toda base existente, não "nenhuma".
     */
    origins: BudgetOriginRef[];
};

/** Recorte por subcategoria indexado por categoria (ADR 0110). Categoria ausente = inteira. */
export function subcategoryScopeByCategory(
    refs: { categoryId: string; subcategoryId: string }[],
): Map<string, Set<string>> {
    const scope = new Map<string, Set<string>>();
    for (const ref of refs) {
        const set = scope.get(ref.categoryId) ?? new Set<string>();
        set.add(ref.subcategoryId);
        scope.set(ref.categoryId, set);
    }
    return scope;
}

/**
 * Uma linha de gasto pertence ao recorte por subcategoria? Categoria sem recorte passa
 * inteira (inclusive o gasto SEM subcategoria). Categoria com recorte só passa o gasto
 * de uma subcategoria listada — o sem subcategoria fica de fora, é o ponto do recorte.
 */
export function rowInSubcategoryScope(
    row: { categoryId: string | null; subcategoryId: string | null },
    scope: Map<string, Set<string>>,
): boolean {
    if (scope.size === 0 || !row.categoryId) return true;
    const allowed = scope.get(row.categoryId);
    if (!allowed) return true;
    return !!row.subcategoryId && allowed.has(row.subcategoryId);
}

/** Chave canônica de uma Origem — o par tipo+id, que é o que compara entre orçamentos. */
export function originKey(origin: { kind: 'account' | 'card'; id: string }): string {
    return `${origin.kind}:${origin.id}`;
}

/**
 * Uma linha de gasto pertence ao recorte? Conjunto vazio significa **todas as origens**,
 * então tudo passa — é o que mantém o orçamento clássico com o número de sempre.
 */
function rowInOrigins(
    row: { accountId: string | null; cardId: string | null },
    originKeys: Set<string>,
): boolean {
    if (originKeys.size === 0) return true;
    if (row.accountId && originKeys.has(`account:${row.accountId}`)) return true;
    if (row.cardId && originKeys.has(`card:${row.cardId}`)) return true;
    return false;
}

/**
 * Uma linha do recorte por subcategoria no acompanhamento. `subcategoryId: null` é o
 * envelope residual "Demais lançamentos" — calculado, nunca gravado.
 */
export interface BudgetLimitLine {
    subcategoryId: string | null;
    /** `null` no residual: quem renderiza traduz o rótulo. */
    name: string | null;
    limit: number;
    spent: number;
}

export type BudgetWithProgress = BudgetWithCategories & {
    /** O ciclo a que `spent` se refere (ADR 0120) — a janela que a tela deve exibir. */
    cycle: BudgetCycle;
    spent: number;
    remaining: number;
    percentage: number;
    /** Gasto por categoria dentro do orçamento (pra quebra no detalhe). */
    categorySpending: { categoryId: string; spent: number }[];
    /**
     * Linhas do recorte por subcategoria + o residual no fim. Vazio quando o orçamento
     * não tem limite nenhum. Por construção as linhas somam exatamente o realizado.
     */
    limitLines: BudgetLimitLine[];
    /**
     * Quanto a soma dos limites passa do teto (0 quando cabe). Acontece ao BAIXAR o teto
     * depois: ADR 0067 preserva os valores digitados e avisa, em vez de reduzir sozinho.
     */
    limitsOverflow: number;
    /** Nomes das subcategorias com gasto que caem no residual (legenda da linha). */
    residualSubcategoryNames: string[];
};

/** Dados de entrada do form (o orçamento tem nome/ícone/cor próprios + N categorias). */
export interface BudgetInput {
    name: string;
    icon: string;
    color: string;
    amount: number;
    /** Cadência do ciclo (ADR 0120): 'monthly' | 'biweekly' | 'weekly'. */
    period: string;
    /** Dia de início do ciclo mensal (1–31). Ignorado nas outras cadências. */
    cycleStartDay?: number;
    /** Data de referência do ciclo semanal/quinzenal. Ignorada no mensal. */
    cycleAnchorDate?: string | null;
    categoryIds: string[];
    /** 'expense' (teto de gasto, default) ou 'income' (meta de receita). ADR 0046. */
    type?: 'expense' | 'income';
    // `alertThreshold` saiu daqui em 18/09/2026: era gravado (0.8) e lido por ninguém. A coluna
    // `alert_threshold` fica inerte no default — ver db/schema.ts e lib/__tests__/deadConfig.test.ts.
    /**
     * Limites por subcategoria (ADR 0067) — opcional e só em orçamento de despesa.
     * Passar a lista SUBSTITUI as linhas do orçamento; `[]` apaga todas.
     */
    subcategoryLimits?: { subcategoryId: string; amount: number }[];
    /**
     * Subcategorias em que o orçamento vigia (ADR 0110) — opcional e só em teto de gasto.
     * Passar a lista SUBSTITUI o recorte; `[]` volta cada categoria a contar inteira.
     * Limite de subcategoria fora do recorte é descartado: não faz sentido tetar o que
     * não conta.
     */
    subcategoryIds?: string[];
    /**
     * Origens do recorte (ADR 0103) — opcional. Passar a lista SUBSTITUI o recorte;
     * `[]` volta o orçamento a valer em **todas** as origens.
     */
    origins?: BudgetOriginInput[];
}

function dedupe(ids: string[]): string[] {
    return [...new Set(ids.filter(Boolean))];
}

/** Carrega as categorias (ordenadas por nome) de um conjunto de orçamentos. */
async function loadCategoriesForBudgets(budgetIds: string[]): Promise<Map<string, BudgetCategoryRef[]>> {
    const map = new Map<string, BudgetCategoryRef[]>();
    if (budgetIds.length === 0) return map;

    const rows = await db
        .select({
            budgetId: budgetCategories.budgetId,
            categoryId: budgetCategories.categoryId,
            name: categories.name,
            color: categories.color,
            icon: categories.icon,
        })
        .from(budgetCategories)
        .innerJoin(categories, eq(budgetCategories.categoryId, categories.id))
        .where(inArray(budgetCategories.budgetId, budgetIds))
        .orderBy(categories.name);

    for (const row of rows) {
        const list = map.get(row.budgetId) ?? [];
        list.push({ categoryId: row.categoryId, name: row.name, color: row.color, icon: row.icon });
        map.set(row.budgetId, list);
    }
    return map;
}

async function insertBudgetCategories(budgetId: string, categoryIds: string[]): Promise<void> {
    const now = new Date().toISOString();
    for (const categoryId of categoryIds) {
        await db.insert(budgetCategories).values({ id: generateId(), budgetId, categoryId, createdAt: now });
    }
}

/**
 * Carrega as Origens (ADR 0103) de um conjunto de orçamentos, com nome/ícone/cor pro chip.
 *
 * Conta ou cartão **arquivado ou excluído continua na lista**, de propósito: o vínculo
 * sobrevive ao soft-delete e o realizado daquele pedaço vira 0, em vez de o recorte
 * esvaziar e o orçamento alargar sozinho de "só o cartão" para "tudo". Duas queries em vez
 * de um UNION porque nome/ícone/cor moram em tabelas diferentes.
 */
async function loadOriginsForBudgets(budgetIds: string[]): Promise<Map<string, BudgetOriginRef[]>> {
    const map = new Map<string, BudgetOriginRef[]>();
    if (budgetIds.length === 0) return map;

    const push = (budgetId: string, origin: BudgetOriginRef) => {
        const list = map.get(budgetId) ?? [];
        list.push(origin);
        map.set(budgetId, list);
    };

    const accountRows = await db
        .select({
            budgetId: budgetOrigins.budgetId,
            id: accounts.id,
            name: accounts.name,
            institution: accounts.institution,
            institutionIcon: accounts.institutionIcon,
        })
        .from(budgetOrigins)
        .innerJoin(accounts, eq(budgetOrigins.accountId, accounts.id))
        .where(inArray(budgetOrigins.budgetId, budgetIds))
        .orderBy(accounts.name);
    for (const row of accountRows) {
        push(row.budgetId, {
            kind: 'account',
            id: row.id,
            name: row.name,
            institution: row.institution,
            institutionIcon: row.institutionIcon,
            brand: null,
        });
    }

    const cardRows = await db
        .select({
            budgetId: budgetOrigins.budgetId,
            id: creditCards.id,
            name: creditCards.name,
            institution: creditCards.institution,
            institutionIcon: creditCards.institutionIcon,
            brand: creditCards.brand,
        })
        .from(budgetOrigins)
        .innerJoin(creditCards, eq(budgetOrigins.creditCardId, creditCards.id))
        .where(inArray(budgetOrigins.budgetId, budgetIds))
        .orderBy(creditCards.name);
    for (const row of cardRows) {
        push(row.budgetId, {
            kind: 'card',
            id: row.id,
            name: row.name,
            institution: row.institution,
            institutionIcon: row.institutionIcon,
            brand: row.brand,
        });
    }

    return map;
}

/**
 * Grava o recorte do orçamento. Lista vazia = **todas** as origens (apaga as linhas), que é
 * o orçamento clássico. Conta e Cartão são excludentes por linha, espelhando o lançamento.
 */
async function replaceOrigins(budgetId: string, origins: BudgetOriginInput[]): Promise<void> {
    await db.delete(budgetOrigins).where(eq(budgetOrigins.budgetId, budgetId));

    const wanted = new Map<string, BudgetOriginInput>();
    for (const origin of origins) {
        if (!origin?.id) continue;
        if (origin.kind !== 'account' && origin.kind !== 'card') continue;
        wanted.set(originKey(origin), origin);
    }
    if (wanted.size === 0) return;

    const now = new Date().toISOString();
    for (const origin of wanted.values()) {
        await db.insert(budgetOrigins).values({
            id: generateId(),
            budgetId,
            accountId: origin.kind === 'account' ? origin.id : null,
            creditCardId: origin.kind === 'card' ? origin.id : null,
            createdAt: now,
        });
    }
}

/**
 * Carrega o recorte por subcategoria (ADR 0110) de um conjunto de orçamentos, com nome e
 * categoria pra agrupar e pro chip.
 *
 * Subcategoria excluída (soft delete) **continua na lista**, como a Origem arquivada: o
 * `deleteSubcategory` já desvincula os lançamentos dela, então o realizado daquela linha
 * vira 0 — em vez de a linha sumir e, se era a única, a categoria voltar a contar inteira
 * calada. O sheet não a lista, então a próxima gravação a descarta.
 */
async function loadSubcategoriesForBudgets(budgetIds: string[]): Promise<Map<string, BudgetSubcategoryRef[]>> {
    const map = new Map<string, BudgetSubcategoryRef[]>();
    if (budgetIds.length === 0) return map;

    const rows = await db
        .select({
            budgetId: budgetSubcategories.budgetId,
            subcategoryId: budgetSubcategories.subcategoryId,
            categoryId: subcategories.categoryId,
            name: subcategories.name,
        })
        .from(budgetSubcategories)
        .innerJoin(subcategories, eq(budgetSubcategories.subcategoryId, subcategories.id))
        .where(inArray(budgetSubcategories.budgetId, budgetIds))
        .orderBy(subcategories.name);

    for (const row of rows) {
        const list = map.get(row.budgetId) ?? [];
        list.push({ subcategoryId: row.subcategoryId, categoryId: row.categoryId, name: row.name });
        map.set(row.budgetId, list);
    }
    return map;
}

/**
 * Grava o recorte por subcategoria: só subcategoria VIVA de uma das categorias do
 * orçamento. Lista vazia = toda categoria conta inteira (apaga as linhas).
 */
async function replaceSubcategories(
    budgetId: string,
    categoryIds: string[],
    subcategoryIds: string[],
): Promise<void> {
    await db.delete(budgetSubcategories).where(eq(budgetSubcategories.budgetId, budgetId));

    const wanted = dedupe(subcategoryIds);
    if (wanted.length === 0 || categoryIds.length === 0) return;

    const valid = await db
        .select({ id: subcategories.id })
        .from(subcategories)
        .where(
            and(
                inArray(subcategories.id, wanted),
                inArray(subcategories.categoryId, categoryIds),
                isNull(subcategories.deletedAt),
            ),
        );

    const now = new Date().toISOString();
    for (const row of valid) {
        await db.insert(budgetSubcategories).values({ id: generateId(), budgetId, subcategoryId: row.id, createdAt: now });
    }
}

/** Recorte gravado de UM orçamento, já indexado por categoria. */
async function loadScope(budgetId: string): Promise<Map<string, Set<string>>> {
    return subcategoryScopeByCategory((await loadSubcategoriesForBudgets([budgetId])).get(budgetId) ?? []);
}

/**
 * Carrega os Limites por subcategoria (ordenados por nome) de um conjunto de orçamentos.
 * Subcategoria excluída (soft delete) sai da lista — a linha morre junto com ela.
 */
async function loadLimitsForBudgets(budgetIds: string[]): Promise<Map<string, BudgetSubcategoryLimitRef[]>> {
    const map = new Map<string, BudgetSubcategoryLimitRef[]>();
    if (budgetIds.length === 0) return map;

    const rows = await db
        .select({
            budgetId: budgetSubcategoryLimits.budgetId,
            subcategoryId: budgetSubcategoryLimits.subcategoryId,
            categoryId: subcategories.categoryId,
            name: subcategories.name,
            amount: budgetSubcategoryLimits.amount,
        })
        .from(budgetSubcategoryLimits)
        .innerJoin(subcategories, eq(budgetSubcategoryLimits.subcategoryId, subcategories.id))
        .where(and(inArray(budgetSubcategoryLimits.budgetId, budgetIds), isNull(subcategories.deletedAt)))
        .orderBy(subcategories.name);

    for (const row of rows) {
        const list = map.get(row.budgetId) ?? [];
        list.push({
            subcategoryId: row.subcategoryId,
            categoryId: row.categoryId,
            name: row.name,
            amount: row.amount,
        });
        map.set(row.budgetId, list);
    }
    return map;
}

/**
 * A linha só existe onde o pai já cobre a categoria dela (ADR 0067) — tirar a categoria
 * do orçamento leva junto os limites das subcategorias dela.
 */
function limitsInsideCategories(
    limits: BudgetSubcategoryLimitRef[],
    categoryIds: string[],
): BudgetSubcategoryLimitRef[] {
    const allowed = new Set(categoryIds);
    return limits.filter((l) => allowed.has(l.categoryId));
}

/** Um limite só vale em subcategoria que CONTA (ADR 0110): fora do recorte, cai. */
function limitsInsideScope(
    limits: BudgetSubcategoryLimitRef[],
    scope: Map<string, Set<string>>,
): BudgetSubcategoryLimitRef[] {
    return limits.filter((l) => rowInSubcategoryScope(l, scope));
}

/** Junta categorias + recorte + limites num orçamento, já podando linha órfã de categoria removida. */
function withRefs(
    budget: Budget,
    catMap: Map<string, BudgetCategoryRef[]>,
    limitMap: Map<string, BudgetSubcategoryLimitRef[]>,
    originMap: Map<string, BudgetOriginRef[]>,
    scopeMap: Map<string, BudgetSubcategoryRef[]>,
): BudgetWithCategories {
    const cats = catMap.get(budget.id) ?? [];
    const categoryIds = cats.map((c) => c.categoryId);
    // Meta de receita não tem recorte por subcategoria nem limite: lá ultrapassar é bom,
    // a cor inverteria (ADR 0067) — e o sheet é um só pros dois (ADR 0110).
    const scoped = budget.type === 'income'
        ? []
        : (scopeMap.get(budget.id) ?? []).filter((s) => categoryIds.includes(s.categoryId));
    const limits = budget.type === 'income'
        ? []
        : limitsInsideScope(
            limitsInsideCategories(limitMap.get(budget.id) ?? [], categoryIds),
            subcategoryScopeByCategory(scoped),
        );
    // A Origem, ao contrário do Limite por subcategoria, vale nos DOIS tipos (ADR 0103):
    // "meta de receita: R$ 5.000 na Conta PJ" é legítimo.
    return {
        ...budget,
        categories: cats,
        subcategoryLimits: limits,
        subcategories: scoped,
        origins: originMap.get(budget.id) ?? [],
    };
}

/**
 * Grava as linhas do orçamento: só subcategoria viva de uma das categorias dele e só
 * valor positivo (limite zerado = linha apagada, é como o form "remove" um limite).
 */
async function replaceSubcategoryLimits(
    budgetId: string,
    categoryIds: string[],
    limits: { subcategoryId: string; amount: number }[],
): Promise<void> {
    await db.delete(budgetSubcategoryLimits).where(eq(budgetSubcategoryLimits.budgetId, budgetId));

    const wanted = new Map<string, number>();
    for (const limit of limits) {
        if (!limit.subcategoryId || !(limit.amount > 0)) continue;
        wanted.set(limit.subcategoryId, limit.amount);
    }
    if (wanted.size === 0 || categoryIds.length === 0) return;

    // Limite só em subcategoria que CONTA (ADR 0110): fora do recorte, descarta.
    const scope = await loadScope(budgetId);
    const valid = (await db
        .select({ id: subcategories.id, categoryId: subcategories.categoryId })
        .from(subcategories)
        .where(
            and(
                inArray(subcategories.id, [...wanted.keys()]),
                inArray(subcategories.categoryId, categoryIds),
                isNull(subcategories.deletedAt),
            ),
        )).filter((row) => rowInSubcategoryScope({ categoryId: row.categoryId, subcategoryId: row.id }, scope));

    const now = new Date().toISOString();
    for (const row of valid) {
        await db.insert(budgetSubcategoryLimits).values({
            id: generateId(),
            budgetId,
            subcategoryId: row.id,
            amount: wanted.get(row.id)!,
            createdAt: now,
            updatedAt: now,
        });
    }
}

/** Quantidade de orçamentos ativos (não-excluídos) — usado pelo soft-limit do plano grátis. */
export async function countBudgets(): Promise<number> {
    const [row] = await db
        .select({ count: sql<number>`COUNT(*)` })
        .from(budgets)
        .where(eq(budgets.isActive, 1));
    return row?.count ?? 0;
}

export async function getBudgets(): Promise<BudgetWithCategories[]> {
    const rows = await db
        .select()
        .from(budgets)
        .where(eq(budgets.isActive, 1))
        .orderBy(budgets.name);

    const ids = rows.map((b) => b.id);
    const catMap = await loadCategoriesForBudgets(ids);
    const limitMap = await loadLimitsForBudgets(ids);
    const originMap = await loadOriginsForBudgets(ids);
    const scopeMap = await loadSubcategoriesForBudgets(ids);
    return rows.map((b) => withRefs(b, catMap, limitMap, originMap, scopeMap));
}

export async function getBudgetById(id: string): Promise<BudgetWithCategories | null> {
    const rows = await db.select().from(budgets).where(eq(budgets.id, id));
    const budget = rows[0];
    // React Query proíbe `undefined` no retorno — devolve null quando não existe.
    if (!budget) return null;

    const catMap = await loadCategoriesForBudgets([id]);
    const limitMap = await loadLimitsForBudgets([id]);
    const originMap = await loadOriginsForBudgets([id]);
    const scopeMap = await loadSubcategoriesForBudgets([id]);
    return withRefs(budget, catMap, limitMap, originMap, scopeMap);
}

/**
 * Categorias já usadas por algum orçamento ativo — só pra INFORMAR no seletor ("já usada
 * em outro orçamento"); desde o ADR 0109 nada é bloqueado. `excludeBudgetId` ignora o
 * orçamento sendo editado, pra ele não marcar as próprias categorias.
 */
export async function getUsedCategoryIds(excludeBudgetId?: string): Promise<string[]> {
    const rows = await db
        .select({ categoryId: budgetCategories.categoryId, budgetId: budgetCategories.budgetId })
        .from(budgetCategories)
        .innerJoin(budgets, eq(budgetCategories.budgetId, budgets.id))
        .where(eq(budgets.isActive, 1));

    return rows.filter((r) => r.budgetId !== excludeBudgetId).map((r) => r.categoryId);
}

export async function createBudget(input: BudgetInput): Promise<Budget> {
    const categoryIds = dedupe(input.categoryIds);
    if (categoryIds.length === 0) throw new ExpectedError(i18n.t('ui:errors.categoryRequired'));

    // Sem checagem de sobreposição: a mesma categoria (e a mesma origem) pode estar em
    // quantos orçamentos a pessoa quiser (ADR 0109). Vigiar duas vezes é escolha dela.
    const origins = input.origins ?? [];

    const id = generateId();
    const now = new Date().toISOString();
    const cycle = normalizeCycleSpec(input);

    await db.insert(budgets).values({
        id,
        categoryId: categoryIds[0], // categoria principal (compat)
        type: input.type ?? 'expense',
        name: input.name,
        icon: input.icon,
        color: input.color,
        amount: input.amount,
        period: cycle.period,
        cycleStartDay: cycle.cycleStartDay,
        cycleAnchorDate: cycle.cycleAnchorDate,
        createdAt: now,
        updatedAt: now,
    });
    await insertBudgetCategories(id, categoryIds);
    // Só teto de gasto tem recorte por subcategoria (ADR 0110) e limite (ADR 0067) — e o
    // recorte vai ANTES do limite, que só vale em subcategoria que conta.
    if ((input.type ?? 'expense') !== 'income') {
        if (input.subcategoryIds?.length) await replaceSubcategories(id, categoryIds, input.subcategoryIds);
        if (input.subcategoryLimits?.length) await replaceSubcategoryLimits(id, categoryIds, input.subcategoryLimits);
    }
    if (origins.length > 0) await replaceOrigins(id, origins);

    const result = await db.select().from(budgets).where(eq(budgets.id, id));
    return result[0];
}

export async function updateBudget(id: string, input: Partial<BudgetInput>): Promise<void> {
    const categoryIds = input.categoryIds ? dedupe(input.categoryIds) : undefined;

    const patch: Partial<typeof budgets.$inferInsert> = { updatedAt: new Date().toISOString() };
    if (input.type !== undefined) patch.type = input.type;
    if (input.name !== undefined) patch.name = input.name;
    if (input.icon !== undefined) patch.icon = input.icon;
    if (input.color !== undefined) patch.color = input.color;
    if (input.amount !== undefined) patch.amount = input.amount;
    if (categoryIds && categoryIds.length > 0) patch.categoryId = categoryIds[0];
    // O ciclo é normalizado como um todo: trocar a cadência zera a âncora que não se
    // aplica, e o que não veio no patch é lido do orçamento atual.
    if (input.period !== undefined || input.cycleStartDay !== undefined || input.cycleAnchorDate !== undefined) {
        const [current] = await db.select({
            period: budgets.period,
            cycleStartDay: budgets.cycleStartDay,
            cycleAnchorDate: budgets.cycleAnchorDate,
        }).from(budgets).where(eq(budgets.id, id));
        const cycle = normalizeCycleSpec({
            period: input.period ?? current?.period,
            cycleStartDay: input.cycleStartDay ?? current?.cycleStartDay,
            cycleAnchorDate: input.cycleAnchorDate !== undefined ? input.cycleAnchorDate : current?.cycleAnchorDate,
        });
        patch.period = cycle.period;
        patch.cycleStartDay = cycle.cycleStartDay;
        patch.cycleAnchorDate = cycle.cycleAnchorDate;
    }

    await db.update(budgets).set(patch).where(eq(budgets.id, id));

    if (categoryIds && categoryIds.length > 0) {
        await db.delete(budgetCategories).where(eq(budgetCategories.budgetId, id));
        await insertBudgetCategories(id, categoryIds);
    }

    // ANTES do return de meta de receita, de propósito: a Origem vale nos dois tipos
    // (ADR 0103) — só o Limite por subcategoria é exclusivo de teto de gasto.
    if (input.origins !== undefined) await replaceOrigins(id, input.origins);

    // Virar meta de receita apaga os dois recortes por subcategoria: lá não existem
    // Limite (ADR 0067) nem recorte (ADR 0110).
    if (input.type === 'income') {
        await db.delete(budgetSubcategoryLimits).where(eq(budgetSubcategoryLimits.budgetId, id));
        await db.delete(budgetSubcategories).where(eq(budgetSubcategories.budgetId, id));
        return;
    }

    const effectiveCategories = categoryIds
        ?? (await loadCategoriesForBudgets([id])).get(id)?.map((c) => c.categoryId)
        ?? [];

    // Recorte por subcategoria (ADR 0110) ANTES do limite: o limite só vale onde conta.
    if (input.subcategoryIds !== undefined) {
        await replaceSubcategories(id, effectiveCategories, input.subcategoryIds);
    } else if (categoryIds && categoryIds.length > 0) {
        // Sem lista nova, mas o conjunto de categorias mudou: poda a linha órfã.
        const existing = (await loadSubcategoriesForBudgets([id])).get(id) ?? [];
        const orphans = existing.filter((s) => !categoryIds.includes(s.categoryId));
        if (orphans.length > 0) {
            await db.delete(budgetSubcategories).where(
                and(
                    eq(budgetSubcategories.budgetId, id),
                    inArray(budgetSubcategories.subcategoryId, orphans.map((s) => s.subcategoryId)),
                ),
            );
        }
    }

    if (input.subcategoryLimits !== undefined) {
        await replaceSubcategoryLimits(id, effectiveCategories, input.subcategoryLimits);
    } else if ((categoryIds && categoryIds.length > 0) || input.subcategoryIds !== undefined) {
        // Sem lista nova, mas o conjunto de categorias ou o recorte mudou: poda a linha
        // que ficou órfã — de categoria que saiu do pai, ou de subcategoria que deixou
        // de contar (ADR 0110).
        const scope = await loadScope(id);
        const existing = (await loadLimitsForBudgets([id])).get(id) ?? [];
        const orphans = existing.filter(
            (l) => !effectiveCategories.includes(l.categoryId) || !rowInSubcategoryScope(l, scope),
        );
        if (orphans.length > 0) {
            await db.delete(budgetSubcategoryLimits).where(
                and(
                    eq(budgetSubcategoryLimits.budgetId, id),
                    inArray(budgetSubcategoryLimits.subcategoryId, orphans.map((l) => l.subcategoryId)),
                ),
            );
        }
    }
}

export async function deleteBudget(id: string): Promise<void> {
    await db.delete(budgetSubcategoryLimits).where(eq(budgetSubcategoryLimits.budgetId, id));
    await db.delete(budgetSubcategories).where(eq(budgetSubcategories.budgetId, id));
    await db.delete(budgetOrigins).where(eq(budgetOrigins.budgetId, id));
    await db.delete(budgetCategories).where(eq(budgetCategories.budgetId, id));
    await db.delete(budgets).where(eq(budgets.id, id));
}

interface SpendingRow {
    date: string;
    categoryId: string | null;
    subcategoryId: string | null;
    accountId: string | null;
    cardId: string | null;
    total: number;
}

/**
 * Uma query só (ADR 0067) quebrada por categoria, subcategoria, origem (ADR 0103) E DIA:
 * como cada orçamento tem o próprio ciclo (ADR 0120), a janela deixou de ser comum — a
 * agregação cobre o envelope de todos os ciclos e cada orçamento filtra as linhas pelo
 * próprio ciclo depois. Todo o escopo analítico (ignorados, artefatos de fatura, cartão
 * excluído) vem de graça.
 */
async function loadSpendingRows(type: 'expense' | 'income', window: BudgetCycle): Promise<SpendingRow[]> {
    // ADR 0077: orçamento NÃO segue "Parcelamento pelo valor total". Ele é ferramenta de
    // compromisso de caixa — responde "quanto cai neste ciclo", não "quanto eu decidi
    // gastar". Com o colapso, um teto de 300 estourava no mês da âncora (4×100 → spent 400)
    // e ficava folgado nos três seguintes (spent 0), para a mesma compra.
    // Meta de receita (type='income'): o "realizado" soma income no MESMO escopo analítico
    // em competência das demais leituras de receita (sem artefatos de fatura, sem principal
    // de empréstimo a pessoa, sem ignorados).
    return db
        .select({
            date: transactions.date,
            categoryId: transactions.categoryId,
            subcategoryId: transactions.subcategoryId,
            accountId: transactions.accountId,
            cardId: sql<string | null>`${originCardIdExpr()}`.as('cardId'),
            total: sql<number>`COALESCE(SUM(${transactions.amount}), 0)`.as('total'),
        })
        .from(transactions)
        .where(
            and(
                eq(transactions.type, type),
                sql`${transactions.date} >= ${window.startDate}`,
                sql`${transactions.date} <= ${window.endDate}`,
                sql`${transactions.deletedAt} IS NULL`,
                excludeNonAnalyticInvoiceAdjustments(),
                ...(type === 'income' ? [excludePersonalLoanPrincipal()] : []),
                excludeIgnored(),
                excludeDeletedCardTransactions(),
            ),
        )
        .groupBy(
            transactions.date,
            transactions.categoryId,
            transactions.subcategoryId,
            transactions.accountId,
            originCardIdExpr(),
        );
}

/** Soma por categoria e por subcategoria das linhas de UM orçamento, no ciclo dele. */
function scopeSpending(budget: BudgetWithCategories, cycle: BudgetCycle, rows: SpendingRow[]) {
    const originKeys = new Set(budget.origins.map(originKey));
    // Recorte por subcategoria (ADR 0110): mesma agregação, mais um predicado.
    const subScope = subcategoryScopeByCategory(budget.subcategories);
    const byCategory = new Map<string | null, number>();
    const bySubcategory = new Map<string, number>();
    const inScope: SpendingRow[] = [];
    for (const row of rows) {
        if (!dateInCycle(cycle, row.date)) continue;
        if (!rowInOrigins(row, originKeys)) continue;
        if (!rowInSubcategoryScope(row, subScope)) continue;
        inScope.push(row);
        byCategory.set(row.categoryId, (byCategory.get(row.categoryId) ?? 0) + row.total);
        if (row.subcategoryId) {
            bySubcategory.set(row.subcategoryId, (bySubcategory.get(row.subcategoryId) ?? 0) + row.total);
        }
    }
    return { byCategory, bySubcategory, inScope };
}

function todayIso(): string {
    return format(new Date(), 'yyyy-MM-dd');
}

/**
 * Progresso de cada orçamento no ciclo dado por `cycleOf`. É o motor comum da lista/Home
 * (Ciclo do mês) e do detalhe (ciclo a ciclo).
 */
async function computeProgress(
    allBudgets: BudgetWithCategories[],
    cycleOf: (budget: BudgetWithCategories) => BudgetCycle,
): Promise<BudgetWithProgress[]> {
    if (allBudgets.length === 0) return [];
    const cycleByBudget = new Map<string, BudgetCycle>();
    for (const budget of allBudgets) cycleByBudget.set(budget.id, cycleOf(budget));

    const expenseBudgets = allBudgets.filter((b) => b.type !== 'income');
    const incomeBudgets = allBudgets.filter((b) => b.type === 'income');
    const expenseWindow = cyclesEnvelope(expenseBudgets.map((b) => cycleByBudget.get(b.id)!));
    const incomeWindow = cyclesEnvelope(incomeBudgets.map((b) => cycleByBudget.get(b.id)!));
    const [spendingRows, earnedRows] = await Promise.all([
        expenseWindow ? loadSpendingRows('expense', expenseWindow) : Promise.resolve([] as SpendingRow[]),
        incomeWindow ? loadSpendingRows('income', incomeWindow) : Promise.resolve([] as SpendingRow[]),
    ]);

    const scopedByBudget = new Map<string, ReturnType<typeof scopeSpending>>();
    for (const budget of allBudgets) {
        const rows = budget.type === 'income' ? earnedRows : spendingRows;
        scopedByBudget.set(budget.id, scopeSpending(budget, cycleByBudget.get(budget.id)!, rows));
    }

    // Legenda do residual ("Manutenção, Decoração e outros"): as subcategorias COM gasto
    // que não ganharam linha própria. Só busca nome quando há orçamento com recorte.
    const residualSubIdsByBudget = new Map<string, string[]>();
    const subcategoryNames = new Map<string, string>();
    const budgetsWithLimits = allBudgets.filter((b) => b.subcategoryLimits.length > 0);
    if (budgetsWithLimits.length > 0) {
        for (const budget of budgetsWithLimits) {
            const budgetCategoryIds = new Set(budget.categories.map((c) => c.categoryId));
            const limited = new Set(budget.subcategoryLimits.map((l) => l.subcategoryId));
            // Só as linhas DENTRO do recorte: a legenda tem que descrever o mesmo dinheiro
            // que a barra, senão cita subcategoria de gasto que o orçamento nem conta.
            // Soma por subcategoria antes de ordenar: as linhas agora vêm por DIA.
            const totals = new Map<string, number>();
            for (const r of scopedByBudget.get(budget.id)?.inScope ?? []) {
                if (!r.subcategoryId || r.total <= 0 || !r.categoryId) continue;
                if (!budgetCategoryIds.has(r.categoryId) || limited.has(r.subcategoryId)) continue;
                totals.set(r.subcategoryId, (totals.get(r.subcategoryId) ?? 0) + r.total);
            }
            const ids = [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([id]) => id);
            if (ids.length > 0) residualSubIdsByBudget.set(budget.id, ids);
        }
        const needed = [...new Set([...residualSubIdsByBudget.values()].flat())];
        if (needed.length > 0) {
            const nameRows = await db
                .select({ id: subcategories.id, name: subcategories.name })
                .from(subcategories)
                .where(inArray(subcategories.id, needed));
            for (const row of nameRows) subcategoryNames.set(row.id, row.name);
        }
    }

    return allBudgets.map((budget) => {
        const scoped = scopedByBudget.get(budget.id)!;
        const categorySpending = budget.categories.map((c) => ({
            categoryId: c.categoryId,
            spent: scoped.byCategory.get(c.categoryId) ?? 0,
        }));
        const spent = categorySpending.reduce((sum, c) => sum + c.spent, 0);
        const remaining = Math.max(budget.amount - spent, 0);
        const percentage = budget.amount > 0 ? spent / budget.amount : 0;

        const limits = budget.subcategoryLimits;
        const limitLines: BudgetLimitLine[] = [];
        let limitsOverflow = 0;
        if (limits.length > 0) {
            const reserved = limits.reduce((sum, l) => sum + l.amount, 0);
            let reservedSpent = 0;
            for (const limit of limits) {
                const lineSpent = scoped.bySubcategory.get(limit.subcategoryId) ?? 0;
                reservedSpent += lineSpent;
                limitLines.push({
                    subcategoryId: limit.subcategoryId,
                    name: limit.name,
                    limit: limit.amount,
                    spent: lineSpent,
                });
            }
            // Baixar o teto do pai depois fura o cap da entrada: ADR 0067 preserva os
            // valores digitados, zera o residual e avisa em vez de reduzir sozinho.
            limitsOverflow = roundMonetaryValue(Math.max(reserved - budget.amount, 0));
            // "Demais lançamentos" é sempre calculado: o que sobra do teto, recebendo
            // todo gasto sem linha própria — inclusive o SEM subcategoria nenhuma.
            // Arredonda: o residual sai de uma SUBTRAÇÃO e herdaria o resíduo de float
            // (R$ 247,10000000000002) — que apareceria cru na tela.
            limitLines.push({
                subcategoryId: null,
                name: null,
                limit: roundMonetaryValue(Math.max(budget.amount - reserved, 0)),
                spent: roundMonetaryValue(Math.max(spent - reservedSpent, 0)),
            });
        }

        return {
            ...budget,
            cycle: cycleByBudget.get(budget.id)!,
            spent,
            remaining,
            percentage,
            categorySpending,
            limitLines,
            limitsOverflow,
            residualSubcategoryNames: (residualSubIdsByBudget.get(budget.id) ?? [])
                .map((subId) => subcategoryNames.get(subId))
                .filter((name): name is string => !!name),
        };
    });
}

/**
 * Todos os orçamentos no **Ciclo do mês** (ADR 0120): pro mensal, o único ciclo que
 * pertence ao mês; pro semanal/quinzenal, o em curso se o mês é o atual, senão o último
 * do mês. `month` é 'yyyy-MM'; `today` só existe pra teste.
 */
export async function getBudgetsWithSpending(
    month: string,
    today: string = todayIso(),
): Promise<BudgetWithProgress[]> {
    const allBudgets = await getBudgets();
    return computeProgress(allBudgets, (budget) => cycleForMonth(budget, month, today));
}

/** Um orçamento num ciclo específico (o detalhe navega ciclo a ciclo). */
export async function getBudgetProgressForCycle(
    id: string,
    cycle: BudgetCycle,
): Promise<BudgetWithProgress | null> {
    const budget = await getBudgetById(id);
    if (!budget) return null;
    const [progress] = await computeProgress([budget], () => cycle);
    return progress ?? null;
}

export interface BudgetVsActualMonth {
    /** Mês no formato yyyy-MM. */
    month: string;
    /** Fração do mês coberta pelo intervalo (1 = mês cheio; parcial em períodos custom). */
    fraction: number;
    budgeted: number;
    spent: number;
}

export interface BudgetVsActualBudget {
    id: string;
    name: string;
    color: string;
    categories: BudgetCategoryRef[];
    budgeted: number;
    spent: number;
}

export interface BudgetVsActual {
    months: BudgetVsActualMonth[];
    budgets: BudgetVsActualBudget[];
}

/**
 * Fração de cada PERÍODO do mês (ADR 0190) tocado pelo intervalo [startDate, endDate] (dias
 * inclusivos, yyyy-MM-dd), do período que contém a primeira ponta ao que contém a última.
 * Período cheio = 1; pontas parciais de um período personalizado < 1. Com Dia de início 1
 * (padrão) é o mês-calendário de sempre.
 */
export function budgetMonthFractions(
    startDate: string,
    endDate: string,
    monthStartDay: number = CALENDAR_MONTH_START_DAY,
): { month: string; fraction: number }[] {
    const keys = monthKeysBetween(
        monthKeyForDate(startDate, monthStartDay),
        monthKeyForDate(endDate, monthStartDay),
    );
    return keys.map((month) => {
        const cycle = monthCycleForKey(month, monthStartDay);
        const coveredStart = startDate > cycle.startDate ? startDate : cycle.startDate;
        const coveredEnd = endDate < cycle.endDate ? endDate : cycle.endDate;
        const coveredDays = differenceInCalendarDays(parseISO(coveredEnd), parseISO(coveredStart)) + 1;
        return { month, fraction: Math.min(coveredDays / monthCycleLength(cycle), 1) };
    });
}

/**
 * Série "previsto × realizado" por PERÍODO do mês do app (ADR 0190) tocado pelo intervalo,
 * mais o total por orçamento. Com o Ciclo do orçamento (ADR 0120) o mês de um orçamento é o conjunto dos
 * ciclos que pertencem a ele: o previsto é o teto × número de ciclos (1 no mensal; 4 ou 5
 * semanas; 2 quinzenas) e o realizado é o gasto dentro desses ciclos — não mais o teto
 * mensalizado por 52/12. Mês cheio lê os ciclos inteiros mesmo que atravessem a virada;
 * ponta parcial de período personalizado prorrateia o previsto pela fração e corta o
 * realizado nas datas do intervalo. O previsto usa os tetos ATUAIS (não há histórico de
 * orçamento — mudanças valem retroativamente).
 */
export async function getBudgetVsActualByMonth(
    startDate: string,
    endDate: string,
    options?: { monthStartDay?: number },
): Promise<BudgetVsActual> {
    // Só orçamentos de GASTO: meta de receita não entra no "previsto × realizado"
    // de despesa (somaria alvo de receita como se fosse teto de gasto).
    const allBudgets = (await getBudgets()).filter((b) => b.type !== 'income');
    // As chaves da série são as do período do app; cada orçamento continua contribuindo com
    // os ciclos DELE que pertencem a cada chave (ADR 0120) — mesmo critério da Home.
    const monthStartDay = options?.monthStartDay ?? getMonthStartDay();
    const months = budgetMonthFractions(startDate, endDate, monthStartDay);

    // Ciclos de cada orçamento em cada mês da série.
    const cyclesByBudgetMonth = new Map<string, BudgetCycle[]>();
    const allCycles: BudgetCycle[] = [];
    for (const budget of allBudgets) {
        for (const { month } of months) {
            const cycles = cyclesOfMonth(budget, month);
            cyclesByBudgetMonth.set(`${budget.id}|${month}`, cycles);
            allCycles.push(...cycles);
        }
    }

    const budgetedByMonth = new Map<string, number>();
    const budgetedByBudget = new Map<string, number>();
    for (const budget of allBudgets) {
        for (const { month, fraction } of months) {
            const count = cyclesByBudgetMonth.get(`${budget.id}|${month}`)?.length ?? 0;
            const budgeted = budget.amount * count * fraction;
            budgetedByMonth.set(month, (budgetedByMonth.get(month) ?? 0) + budgeted);
            budgetedByBudget.set(budget.id, (budgetedByBudget.get(budget.id) ?? 0) + budgeted);
        }
    }

    const spentByMonth = new Map<string, number>();
    const spentByBudget = new Map<string, number>();
    const envelope = cyclesEnvelope(allCycles);
    if (envelope && allBudgets.length > 0) {
        const partialMonths = new Set(months.filter((m) => m.fraction < 1).map((m) => m.month));
        // Quebra por categoria e origem além do dia (ADR 0103): cada orçamento conta apenas o
        // que cai no recorte dele. A soma é POR ORÇAMENTO, dos dois lados: como orçamentos
        // podem se sobrepor (ADR 0109), um gasto vigiado por dois aparece nos dois — igual
        // ao teto. É a mesma conta do BudgetSummaryCard; o relatório lê "a soma dos
        // orçamentos", não "a categoria".
        const rows = await loadSpendingRows('expense', {
            startDate: envelope.startDate < startDate ? envelope.startDate : startDate,
            endDate: envelope.endDate > endDate ? envelope.endDate : endDate,
        });
        for (const budget of allBudgets) {
            const budgetCategoryIds = new Set(budget.categories.map((c) => c.categoryId));
            const originKeys = new Set(budget.origins.map(originKey));
            const subScope = subcategoryScopeByCategory(budget.subcategories);
            for (const row of rows) {
                if (!row.categoryId || !budgetCategoryIds.has(row.categoryId)) continue;
                if (!rowInOrigins(row, originKeys)) continue;
                if (!rowInSubcategoryScope(row, subScope)) continue;
                const month = cycleMonth(budget, cycleContaining(budget, row.date));
                if (!budgetedByMonth.has(month)) continue;
                // Ponta parcial: só o que está literalmente dentro do intervalo pedido.
                if (partialMonths.has(month) && (row.date < startDate || row.date > endDate)) continue;
                spentByMonth.set(month, (spentByMonth.get(month) ?? 0) + row.total);
                spentByBudget.set(budget.id, (spentByBudget.get(budget.id) ?? 0) + row.total);
            }
        }
    }

    return {
        months: months.map(({ month, fraction }) => ({
            month,
            fraction,
            budgeted: budgetedByMonth.get(month) ?? 0,
            spent: spentByMonth.get(month) ?? 0,
        })),
        budgets: allBudgets.map((budget) => ({
            id: budget.id,
            name: budget.name,
            color: budget.color,
            categories: budget.categories,
            budgeted: budgetedByBudget.get(budget.id) ?? 0,
            spent: spentByBudget.get(budget.id) ?? 0,
        })),
    };
}

/** Um ciclo do retrospecto de UM orçamento (card do detalhe). */
export interface BudgetCyclePoint {
    cycle: BudgetCycle;
    spent: number;
    /** O ciclo terminou antes de hoje? Só ciclo fechado vira veredito. */
    closed: boolean;
    /** O orçamento já existia quando o ciclo começou? Ver lib/budgetHistory.ts. */
    tracked: boolean;
}

/**
 * Realizado de um orçamento em cada ciclo da janela — o mesmo recorte (categorias,
 * origem, subcategoria) e o mesmo escopo analítico do progresso, pra que a última barra
 * do gráfico bata EXATAMENTE com o "Gasto" do card acima dela.
 */
export async function getBudgetCycleHistory(
    id: string,
    cycles: BudgetCycle[],
    today: string = todayIso(),
): Promise<BudgetCyclePoint[]> {
    const budget = await getBudgetById(id);
    const envelope = cyclesEnvelope(cycles);
    if (!budget || !envelope) return [];

    const rows = await loadSpendingRows(budget.type === 'income' ? 'income' : 'expense', envelope);
    const createdOn = budget.createdAt.slice(0, 10);
    return cycles.map((cycle) => {
        const scoped = scopeSpending(budget, cycle, rows);
        let spent = 0;
        for (const category of budget.categories) {
            spent += scoped.byCategory.get(category.categoryId) ?? 0;
        }
        return {
            cycle,
            spent: roundMonetaryValue(spent),
            closed: cycle.endDate < today,
            tracked: createdOn <= cycle.startDate,
        };
    });
}

/** Um mês do retrospecto de um orçamento na tela Histórico. */
export interface BudgetHistoryCell {
    month: string;
    /** Teto × número de ciclos que pertencem ao mês (1 no mensal, 4–5 no semanal). */
    budgeted: number;
    spent: number;
    /** Tem veredito? Exige todos os ciclos do mês fechados E o orçamento já existindo. */
    judged: boolean;
}

export interface BudgetHistoryRow {
    id: string;
    name: string;
    color: string;
    icon: string;
    cells: BudgetHistoryCell[];
    judgedCount: number;
    withinCount: number;
}

export interface BudgetHistory {
    months: string[];
    /** Agregado do que foi julgado em cada mês. `budgeted === 0` = mês sem veredito. */
    totals: { month: string; budgeted: number; spent: number }[];
    budgets: BudgetHistoryRow[];
}

/**
 * Retrospecto mês a mês de todos os tetos de gasto (tela Histórico). Mesma conta do
 * relatório previsto × realizado — teto por CICLO do mês, realizado dentro desses ciclos —
 * com uma diferença: aqui o mês só entra na estatística se o teto de fato governou o
 * período (ver lib/budgetHistory.ts). Meta de receita fica de fora: "estourou o teto" não
 * é veredito de receita.
 */
export async function getBudgetHistoryByMonth(
    months: string[],
    today: string = todayIso(),
): Promise<BudgetHistory> {
    const allBudgets = (await getBudgets()).filter((b) => b.type !== 'income');
    const empty: BudgetHistory = {
        months,
        totals: months.map((month) => ({ month, budgeted: 0, spent: 0 })),
        budgets: [],
    };
    if (allBudgets.length === 0 || months.length === 0) return empty;

    // Ciclos de cada orçamento em cada mês da janela, e o envelope que cobre todos eles
    // (uma query só — os ciclos atravessam a virada quando o dia de início não é 1).
    const cyclesByBudgetMonth = new Map<string, BudgetCycle[]>();
    const allCycles: BudgetCycle[] = [];
    for (const budget of allBudgets) {
        for (const month of months) {
            const cycles = cyclesOfMonth(budget, month);
            cyclesByBudgetMonth.set(`${budget.id}|${month}`, cycles);
            allCycles.push(...cycles);
        }
    }
    const envelope = cyclesEnvelope(allCycles);
    if (!envelope) return empty;

    const rows = await loadSpendingRows('expense', envelope);
    const monthSet = new Set(months);
    const spentByBudgetMonth = new Map<string, number>();
    for (const budget of allBudgets) {
        const budgetCategoryIds = new Set(budget.categories.map((c) => c.categoryId));
        const originKeys = new Set(budget.origins.map(originKey));
        const subScope = subcategoryScopeByCategory(budget.subcategories);
        for (const row of rows) {
            if (!row.categoryId || !budgetCategoryIds.has(row.categoryId)) continue;
            if (!rowInOrigins(row, originKeys)) continue;
            if (!rowInSubcategoryScope(row, subScope)) continue;
            const month = cycleMonth(budget, cycleContaining(budget, row.date));
            if (!monthSet.has(month)) continue;
            const key = `${budget.id}|${month}`;
            spentByBudgetMonth.set(key, (spentByBudgetMonth.get(key) ?? 0) + row.total);
        }
    }

    const totalsByMonth = new Map<string, { budgeted: number; spent: number }>();
    for (const month of months) totalsByMonth.set(month, { budgeted: 0, spent: 0 });

    const budgetRows = allBudgets.map((budget) => {
        const createdOn = budget.createdAt.slice(0, 10);
        let judgedCount = 0;
        let withinCount = 0;
        const cells = months.map((month) => {
            const cycles = cyclesByBudgetMonth.get(`${budget.id}|${month}`) ?? [];
            const budgeted = roundMonetaryValue(budget.amount * cycles.length);
            const spent = roundMonetaryValue(spentByBudgetMonth.get(`${budget.id}|${month}`) ?? 0);
            const judged = cycles.length > 0
                && cycles.every((cycle) => cycle.endDate < today)
                && createdOn <= cycles[0].startDate;
            if (judged) {
                judgedCount += 1;
                if (spent <= budgeted) withinCount += 1;
                const total = totalsByMonth.get(month)!;
                total.budgeted += budgeted;
                total.spent += spent;
            }
            return { month, budgeted, spent, judged };
        });
        return {
            id: budget.id,
            name: budget.name,
            color: budget.color,
            icon: budget.icon,
            cells,
            judgedCount,
            withinCount,
        };
    });

    return {
        months,
        totals: months.map((month) => {
            const total = totalsByMonth.get(month)!;
            return {
                month,
                budgeted: roundMonetaryValue(total.budgeted),
                spent: roundMonetaryValue(total.spent),
            };
        }),
        budgets: budgetRows,
    };
}
